
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { ArrowRight, Loader2 } from 'lucide-react';
import { loadStripe } from '@stripe/stripe-js';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

// Initialize Stripe with the public key
const stripePromise = loadStripe('pk_test_51QrifIDOwZmfAjLr37vrdr1hyPd0dRoGWz8JUOpB4ILTaeDzqYrGklu067m7Eg0yD5U8F2vNQllNOsfLF7WVkEk100avbNKNga');

export const PremiumFeatures: React.FC = () => {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);

  const handleUpgradeClick = async () => {
    try {
      setIsLoading(true);
      console.log('Starting upgrade process...');

      // Get current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError) {
        console.error('Auth error:', userError);
        throw new Error('Authentication failed');
      }
      
      if (!user) {
        console.error('No user found');
        throw new Error('User not authenticated');
      }

      console.log('User authenticated:', user.email);

      // Create Stripe checkout session
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: {
          customerEmail: user.email,
          customerId: user.id,
        },
      });

      if (error) {
        console.error('Function error:', error);
        throw new Error(error.message || 'Failed to create checkout session');
      }

      if (!data?.sessionId) {
        console.error('No session ID returned:', data);
        throw new Error('Invalid checkout session');
      }

      console.log('Checkout session created:', data.sessionId);

      // Redirect to Stripe checkout
      const stripe = await stripePromise;
      if (!stripe) {
        throw new Error('Stripe failed to load');
      }

      const { error: stripeError } = await stripe.redirectToCheckout({
        sessionId: data.sessionId,
      });

      if (stripeError) {
        console.error('Stripe redirect error:', stripeError);
        throw stripeError;
      }

    } catch (error: any) {
      console.error('Upgrade error:', error);
      toast({
        title: 'Error',
        description: error.message || 'Unable to process upgrade request. Please try again or contact support.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-gradient-to-br from-green-50/80 to-green-50 p-8 rounded-xl border border-green-100 shadow-soft">
      <div className="space-y-6">
        <div className="space-y-3">
          <h3 className="text-2xl font-semibold text-gray-900">Unlock Premium Features</h3>
          <p className="text-gray-600 leading-relaxed">
            Gain full control over your PEO relationship with advanced pricing tracking, seamless provider transitions, and dedicated support.
          </p>
        </div>
        
        <div className="space-y-4">
          <div className="glass-card p-4 backdrop-blur-sm border-white/30 hover:shadow-md transition-all duration-300">
            <h4 className="font-semibold text-gray-900 mb-1">Real-Time Pricing Monitoring</h4>
            <p className="text-gray-600">Ensure your actual costs match your proposal and stay informed about any changes.</p>
          </div>
          
          <div className="glass-card p-4 backdrop-blur-sm border-white/30 hover:shadow-md transition-all duration-300">
            <h4 className="font-semibold text-gray-900 mb-1">Independent Record-Keeping</h4>
            <p className="text-gray-600">Securely store and update data outside your PEO's system for leverage, easy access, and flexibility.</p>
          </div>
          
          <div className="glass-card p-4 backdrop-blur-sm border-white/30 hover:shadow-md transition-all duration-300">
            <h4 className="font-semibold text-gray-900 mb-1">Effortless Transitions</h4>
            <p className="text-gray-600">Quickly compare new quotes and switch PEOs without administrative headaches.</p>
          </div>
        </div>

        <div className="space-y-4">
          <p className="text-center font-semibold text-lg text-gray-900">
            Upgrade to Premium – $200/month or $2,000/year
          </p>
          <Button
            variant="glass-primary"
            className="w-full py-6 text-lg shadow-xl shadow-primary/20 hover:shadow-primary/30 transition-all duration-300"
            onClick={handleUpgradeClick}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                Upgrade to Premium
                <ArrowRight className="ml-2 h-5 w-5" />
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};
